// ImageUtils.js

export const readFile = (file) => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.addEventListener('load', () => resolve(reader.result));
        reader.readAsDataURL(file);
    });
};

export const getCroppedImg = (imageSrc, crop) => {
    const canvas = document.createElement('canvas');
    const image = new Image();
    image.crossOrigin = "anonymous"; // Esta línea es clave para evitar problemas CORS
    return new Promise((resolve, reject) => {
        image.src = imageSrc;
        image.onload = () => {
            const ctx = canvas.getContext('2d');

            // Define canvas size to the crop size
            canvas.width = crop.width;
            canvas.height = crop.height;

            // Draw the image cropped on the canvas
            ctx.drawImage(
                image,
                crop.x,
                crop.y,
                crop.width,
                crop.height,
                0,
                0,
                crop.width,
                crop.height
            );

            // Convert canvas to blob and create a file
            canvas.toBlob((blob) => {
                if (!blob) {
                    reject(new Error('Canvas is empty'));
                    return;
                }
                const file = new File([blob], 'cropped.jpg', { type: 'image/jpeg' });
                resolve({ file, url: URL.createObjectURL(blob) });
            }, 'image/jpeg');
        };

        image.onerror = (err) => {
            reject(err);
        };
    });
};

export const createResizedImageFile = (
    file,
    {
        width,
        height,
        mimeType = 'image/webp',
        quality = 0.82,
        fileName = 'preview.webp'
    }
) => {
    return new Promise((resolve, reject) => {
        const image = new Image();
        const objectUrl = URL.createObjectURL(file);

        image.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            if (!ctx) {
                URL.revokeObjectURL(objectUrl);
                reject(new Error('Canvas context unavailable'));
                return;
            }

            const sourceRatio = image.width / image.height;
            const targetRatio = width / height;

            let sourceWidth = image.width;
            let sourceHeight = image.height;
            let sourceX = 0;
            let sourceY = 0;

            if (sourceRatio > targetRatio) {
                sourceWidth = image.height * targetRatio;
                sourceX = (image.width - sourceWidth) / 2;
            } else {
                sourceHeight = image.width / targetRatio;
                sourceY = (image.height - sourceHeight) / 2;
            }

            ctx.drawImage(
                image,
                sourceX,
                sourceY,
                sourceWidth,
                sourceHeight,
                0,
                0,
                width,
                height
            );

            canvas.toBlob((blob) => {
                URL.revokeObjectURL(objectUrl);

                if (!blob) {
                    reject(new Error('Preview generation failed'));
                    return;
                }

                resolve(new File([blob], fileName, { type: mimeType }));
            }, mimeType, quality);
        };

        image.onerror = (error) => {
            URL.revokeObjectURL(objectUrl);
            reject(error);
        };

        image.src = objectUrl;
    });
};

export const base64ToBlob = (base64, mimeType = "application/pdf") => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
};

/**
 * Recorta [imageSrc] al área [crop] (en píxeles) y devuelve un PNG (conserva transparencia).
 * Si el recorte supera maxWidth x maxHeight se reduce manteniendo la proporción; nunca se agranda.
 */
export const getCroppedPng = (imageSrc, crop, { maxWidth, maxHeight, fileName = 'logo.png' } = {}) =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => {
            const scale = Math.min(1, (maxWidth || Infinity) / crop.width, (maxHeight || Infinity) / crop.height);
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(crop.width * scale));
            canvas.height = Math.max(1, Math.round(crop.height * scale));
            const ctx = canvas.getContext('2d');
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, canvas.width, canvas.height);
            canvas.toBlob((blob) => {
                if (!blob) {
                    reject(new Error('Canvas is empty'));
                    return;
                }
                resolve(new File([blob], fileName, { type: 'image/png' }));
            }, 'image/png');
        };
        image.onerror = reject;
        image.src = imageSrc;
    });

// Recorta el área elegida y la deja como PNG cuadrado de `size` px (conserva la transparencia).
export const getCroppedSquarePng = (imageSrc, crop, size = 192, fileName = 'favicon.png') => {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext('2d');
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, size, size);
            canvas.toBlob((blob) => {
                if (!blob) {
                    reject(new Error('Canvas is empty'));
                    return;
                }
                resolve(new File([blob], fileName, { type: 'image/png' }));
            }, 'image/png');
        };
        image.onerror = reject;
        image.src = imageSrc;
    });
};
